import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";
import { MagneticButton } from "./MagneticButton";

const LINKS = [
  { label: "Brands", id: "brands" },
  { label: "Personas", id: "personas" },
  { label: "Technology", id: "technology" },
  { label: "Database", id: "database" },
];

export const Nav = ({ onScrollTo }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    onScrollTo(id);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 1.4, ease: [0.22, 1, 0.36, 1] }}
      data-testid="main-nav"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-black/50 backdrop-blur-md border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-[1600px] items-center justify-between px-6 py-5 md:px-12">
        <Logo className="h-8 md:h-10" onClick={() => go("top")} />

        <nav className="hidden items-center gap-10 md:flex">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => go(l.id)}
              className="font-body text-[13px] uppercase tracking-[0.18em] text-white/60 transition-colors duration-300 hover:text-white"
            >
              {l.label}
            </button>
          ))}
          <MagneticButton
            testId="nav-book-demo"
            onClick={() => go("contact")}
            className="rounded-full border border-white/20 px-6 py-2.5 font-body text-[13px] uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:border-teal hover:bg-teal hover:text-black"
          >
            Book a Demo
          </MagneticButton>
        </nav>

        <button
          className="md:hidden text-white"
          onClick={() => setOpen((o) => !o)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-white/5 bg-obsidian md:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-nav-${l.id}`}
                  onClick={() => go(l.id)}
                  className="py-3 text-left font-display text-2xl text-white/80"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="mobile-nav-book-demo"
                onClick={() => go("contact")}
                className="mt-4 rounded-full border border-teal bg-teal px-6 py-3 font-body text-sm uppercase tracking-[0.15em] text-black"
              >
                Book a Demo
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Nav;
